import React, { useState, useEffect } from 'react';
import { Menu, X, Clock, Volume2, VolumeX, Moon, Sun, Sparkles, History, Crown } from 'lucide-react';
import { NavigationItem, AppSettings } from '../types';
import ZodiacClock from './ZodiacClock';
import { playChime } from '../utils/sound';

interface NavbarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  settings?: AppSettings;
  updateSettings?: (s: Partial<AppSettings>) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentPath, onNavigate, settings, updateSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


  // Detect mobile breakpoint (md breakpoint = 768px)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Close menu when transitioning to desktop view
      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems: NavigationItem[] = [
    { label: 'Age Calculator', path: '/', icon: <Clock size={16} /> },
    { label: 'Timeline', path: '/timeline', icon: <History size={16} /> },
    { label: 'Personality', path: '/personality', icon: <Clock size={16} /> }, 
    { label: 'Zodiac', path: '/zodiac', icon: <Clock size={16} /> },
    { label: 'Life Progress', path: '/progress', icon: <Clock size={16} /> },
    { label: 'Tarot', path: '/tarot', icon: <Sparkles size={16} /> },
    { label: 'Chinese Zodiac', path: '/chinese-zodiage', icon: <Sparkles size={16} /> },
  ];

  const handleNav = (path: string) => {
    if (settings?.soundEnabled) playChime();
    onNavigate(path);
    setIsOpen(false);
  };

  const toggleSound = () => {
      if (updateSettings && settings) {
          updateSettings({ soundEnabled: !settings.soundEnabled });
          if (!settings.soundEnabled) playChime();
      }
  };

  const toggleNight = () => {
      if (updateSettings && settings) {
          updateSettings({ nightMode: !settings.nightMode });
          if (settings.soundEnabled) playChime();
      }
  };

  return (
    <div className="relative z-50">
      {/* Main Header - Logo and Desktop Controls */}
      <nav className="bg-transparent py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between md:justify-start md:gap-12 h-28">
            {/* Logo Area */}
            <div className="flex-shrink-0 flex items-center gap-4 cursor-pointer" onClick={() => handleNav('/')}>
              {/* Custom Animated Logo */}
              <ZodiacClock size={90} />
              <span className="font-display font-bold text-5xl text-[#FFC82C] transition-colors duration-1000 tracking-wide">
                ZodiAge
              </span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-baseline space-x-4">
                {navItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => handleNav(item.path)}
                    className={`text-sm font-medium transition-colors hover:text-[var(--season-primary)] ${
                      currentPath === item.path
                        ? 'text-white'
                        : 'text-gray-400'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Global Settings Toggles */}
              <div className="flex items-center gap-3 border-l border-gray-700 pl-4 ml-2">
                  <button 
                      onClick={toggleSound}
                      className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                      title={settings?.soundEnabled ? "Mute Sound" : "Enable Sound"}
                  >
                      {settings?.soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                  </button>
              </div>
              
              {/* CTA Button */}
              <div className="flex gap-2 items-center">
                {/* Premium CTA removed - app is fully free */}
              </div>
            </div>

            {/* Mobile Menu Toggle Button - Visible only on mobile */}
            {isMobile && (
              <div className="flex md:hidden items-center gap-2">
                <button
                    onClick={toggleSound}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                    aria-label="Toggle Sound"
                >
                    {settings?.soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                </button>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white focus:outline-none transition-colors"
                  aria-label={isOpen ? "Close Menu" : "Open Menu"}
                  aria-expanded={isOpen}
                >
                  {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile-Only Navigation Menu - Positioned directly below logo, centered */}
      {isMobile && isOpen && (
        <nav className="bg-gradient-to-b from-cosmic-800 to-cosmic-900 border-t border-cosmic-700 shadow-xl px-4 py-6 w-full">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-3">
            {/* Navigation Items - Stacked and Centered */}
            <div className="w-full max-w-xs flex flex-col space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNav(item.path)}
                  className={`px-4 py-3 rounded-md text-base font-medium transition-all min-h-[44px] flex items-center justify-center ${
                      currentPath === item.path
                        ? 'bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white shadow-lg shadow-purple-600/50'
                        : 'text-gray-300 hover:text-white hover:bg-cosmic-700 active:bg-cosmic-600'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      )}
    </div>
  );
};

export default Navbar;